import { useEffect, useMemo, useState } from "react";
import {
  Accordion, Alert, Button, Group, Paper, ScrollArea, Table, Text, Title,
} from "@mantine/core";
import {
  periodFeatureIssues, type FeatureIssueSummary,
} from "./featureIssueSummaryUtils";

function numberDisplay(value: number): string {
  return value.toFixed(1).replace(/\.0$/, "");
}

function issueDisplay(red: number, yellow: number, runners: number) {
  if (!runners || (!red && !yellow)) return "—";
  return <>
    {red > 0 && <Text span c="red.7" fw={600}>
      R {numberDisplay(100 * red / runners)}% ({red})
    </Text>}
    {red > 0 && yellow > 0 && <Text span c="dimmed"> · </Text>}
    {yellow > 0 && <Text span c="yellow.8" fw={600}>
      Y {numberDisplay(100 * yellow / runners)}% ({yellow})
    </Text>}
  </>;
}

export default function FeatureIssueSummaryTables({
  days, summaries,
}: {
  days: string[];
  summaries: FeatureIssueSummary[];
}) {
  const panels = useMemo(() => periodFeatureIssues(summaries), [summaries]);
  const strategyKey = panels.map((panel) => panel.strategy).join("\u0000");
  const [openStrategies, setOpenStrategies] = useState<string[]>([]);
  useEffect(() => {
    setOpenStrategies(strategyKey ? strategyKey.split("\u0000") : []);
  }, [strategyKey]);

  return <Paper withBorder p="md">
    <Group justify="space-between" mb="sm">
      <Title order={4}>Feature issues by strategy</Title>
      <Group gap="xs">
        <Button size="xs" variant="light"
          onClick={() => setOpenStrategies(panels.map((panel) => panel.strategy))}>
          Open all
        </Button>
        <Button size="xs" variant="light" onClick={() => setOpenStrategies([])}>
          Close all
        </Button>
      </Group>
    </Group>
    {panels.length === 0 ? <Alert color="gray">
      No strategy feature summaries are available for this period.
    </Alert> : <Accordion multiple value={openStrategies}
      onChange={setOpenStrategies} variant="contained">
      {panels.map((panel) => {
        const totalRunners = days.reduce(
          (total, day) => total + (panel.runnersByDay[day] || 0), 0,
        );
        return <Accordion.Item key={panel.strategy} value={panel.strategy}>
          <Accordion.Control>
            <Group justify="space-between" pr="md">
              <Text fw={600}>{panel.strategy}</Text>
              <Text size="xs" c="dimmed">
                {panel.rows.length} issue{panel.rows.length === 1 ? "" : "s"}
              </Text>
            </Group>
          </Accordion.Control>
          <Accordion.Panel>
            {panel.rows.length === 0 ? <Alert color="green">
              No red or yellow feature differences in this period.
            </Alert> : <ScrollArea>
              <Table striped highlightOnHover>
                <Table.Thead><Table.Tr>
                  <Table.Th>Feature</Table.Th>
                  {days.map((day) => <Table.Th key={day}>{day.slice(5)}</Table.Th>)}
                  <Table.Th>Total</Table.Th>
                </Table.Tr></Table.Thead>
                <Table.Tbody>
                  <Table.Tr>
                    <Table.Td><Text fw={700}>Total runners</Text></Table.Td>
                    {days.map((day) => <Table.Td key={day}>
                      <Text fw={700}>{panel.runnersByDay[day] || 0}</Text>
                    </Table.Td>)}
                    <Table.Td><Text fw={700}>{totalRunners}</Text></Table.Td>
                  </Table.Tr>
                  {panel.rows.map((rule) => <Table.Tr
                    key={`${rule.group}-${rule.feature || "group"}`}>
                  <Table.Td style={{ minWidth: 220 }}>{rule.label}</Table.Td>
                  {days.map((day) => {
                    const counts = rule.byDay[day] || { red: 0, yellow: 0 };
                    return <Table.Td key={day} style={{ whiteSpace: "nowrap" }}>
                      {issueDisplay(
                        counts.red, counts.yellow, panel.runnersByDay[day] || 0,
                      )}
                    </Table.Td>;
                  })}
                  <Table.Td style={{ whiteSpace: "nowrap" }}>
                    {issueDisplay(rule.red_count, rule.yellow_count, totalRunners)}
                  </Table.Td>
                  </Table.Tr>)}
                </Table.Tbody>
              </Table>
            </ScrollArea>}
          </Accordion.Panel>
        </Accordion.Item>;
      })}
    </Accordion>}
  </Paper>;
}
